import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CapQuestLogo, EyeIcon } from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { Input } from '../../components/input/index';
import Loader from '../../components/loader/index';
import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { useAppMutation, useAppQuery } from '../../hooks/index';
import { ApiService } from '../../services/index';
import { accessTokenStorage } from '../../storage/auth/index';
import { PageRoute } from '../../types/page.types';
import { passwordRules } from '../register-verify/register-verify';
import { notifyPasswordRecovered } from './password-recovered-event.util';

const PasswordRecovery = () => {
  const navigate = useNavigate();
  const [isPasswordShown, setPasswordShown] = useState(false);
  const [isPasswordVerifyShown, setPasswordVerifyShown] = useState(false);
  const [isRecovered, setIsRecovered] = useState(false);
  const isRecoveredRef = useRef(false);

  const { id } = useParams();

  const [recoverPasswordDto, setRecoverPasswordDto] = useState({
    password: '',
    repeatPassword: '',
  });

  const { isPending: isLoadingIdCheck } = useAppQuery({
    retry: false,
    enabled: Boolean(id) && !isRecovered,
    queryKey: [QueryKey.PASSWORD_RECOVERY_ID_CHECK, id],
    queryFn: () =>
      ApiService.get({ endpoint: BackendRoute.RECOVERY, routePath: ['check', id || ''] }),
    defaultErrorHandling: false,
    onError: () => {
      if (isRecoveredRef.current) return;
      navigate('/error-page');
    },
  });

  const changePasswordMutation = useAppMutation<void, HttpException>([], {
    mutationFn: async () => {
      await ApiService.post(
        { endpoint: BackendRoute.AUTH_RECOVER_PASSWORD },
        {
          body: {
            ...recoverPasswordDto,
            recoveryId: id,
          },
        },
      );
    },
    onSuccess: () => {
      isRecoveredRef.current = true;
      setIsRecovered(true);
      accessTokenStorage.getState().delete();
      notifyPasswordRecovered();
      void ApiService.delete({ endpoint: BackendRoute.AUTH_LOGOUT }).catch(() => undefined);
    },
  });

  const isPasswordValid = passwordRules.every(({ check }) => check(recoverPasswordDto));

  if (isLoadingIdCheck && !isRecovered) return <Loader />;

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto bg-gray-50 font-inter">
      <div className="max-h-full max-lg:h-full max-lg:w-full">
        <div
          className="flex h-fit min-h-full w-full flex-col gap-8 rounded-lg bg-white px-4 pb-8 pt-16 lg:h-fit lg:w-[471px] lg:items-center lg:px-8 lg:pt-8 lg:text-center"
          style={{
            filter:
              'drop-shadow(0px 1px 2px #1018280F) drop-shadow(0px 2px 3px #1018281A) drop-shadow(0px -1px 3px #1018280A)',
          }}
        >
          <CapQuestLogo size={48} />

          <div className="flex flex-col gap-2">
            <div
              className="!bg-clip-text text-4xl-mobile font-bold text-transparent lg:text-4xl"
              style={{
                background: 'linear-gradient(132.59deg, #475467 29.58%, #101828 84.27%)',
              }}
            >
              {isRecovered ? (
                <AppFormattedMessage id="PASSWORD_CHANGED" />
              ) : (
                <AppFormattedMessage id="CHANGE_YOUR_PASSWORD" />
              )}
            </div>
            <div className="text-sm text-gray-500">
              {isRecovered ? (
                <AppFormattedMessage id="PASSWORD_SUCCESSFULLY_CHANGED" />
              ) : (
                <AppFormattedMessage id="ENTER_A_NEW_PASSWORD_BELOW" />
              )}
            </div>
          </div>

          {!isRecovered && (
            <>
              <div className="flex w-full flex-col gap-4">
                <Input
                  icon={<EyeIcon variant={isPasswordShown ? 'ACTIVE' : 'CLOSE'} />}
                  isError={changePasswordMutation.isError}
                  onChange={(value) =>
                    setRecoverPasswordDto({
                      ...recoverPasswordDto,
                      password: value,
                    })
                  }
                  onIconClick={() => setPasswordShown((prev) => !prev)}
                  placeholder={<AppFormattedMessage id="NEW_PASSWORD" />}
                  type={isPasswordShown ? 'text' : 'password'}
                  value={recoverPasswordDto.password}
                />
                <Input
                  errorMessage={changePasswordMutation.error?.body?.message}
                  icon={<EyeIcon variant={isPasswordVerifyShown ? 'ACTIVE' : 'CLOSE'} />}
                  isError={changePasswordMutation.isError}
                  onChange={(value) =>
                    setRecoverPasswordDto({
                      ...recoverPasswordDto,
                      repeatPassword: value,
                    })
                  }
                  onIconClick={() => setPasswordVerifyShown((prev) => !prev)}
                  placeholder={<AppFormattedMessage id="REENTER_NEW_PASSWORD" />}
                  type={isPasswordVerifyShown ? 'text' : 'password'}
                  value={recoverPasswordDto.repeatPassword}
                />
              </div>
              <div className="flex w-full flex-col gap-4 border border-gray-200 p-4 font-inter text-xs text-gray-700">
                <div className="text-left text-sm">
                  <AppFormattedMessage id="PASSWORD_RECOMMENDATION" />
                </div>
                <div className="flex flex-col gap-3">
                  {passwordRules.map(({ title, check }, i) => (
                    <div
                      className={`flex items-center gap-2 ${check(recoverPasswordDto) ? 'text-forest-500' : ''}`}
                      key={i}
                    >
                      <svg
                        fill="none"
                        height="16"
                        viewBox="0 0 16 16"
                        width="16"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.3333 4L6 11.3333L2.66667 8"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.33333"
                        />
                      </svg>
                      <div>{title}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {isRecovered ? (
            <Button onClick={() => navigate(PageRoute.LOGIN)}>
              <AppFormattedMessage id="SIGN_IN_TO_CAPQEUST" />
            </Button>
          ) : (
            <div className="mt-2 w-full">
              <Button
                disabled={!isPasswordValid}
                isLoading={changePasswordMutation.isPending}
                onClick={() => changePasswordMutation.mutate()}
              >
                <AppFormattedMessage id="CHANGE_PASSWORD" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordRecovery;
